import { CopyOutlined } from '@ant-design/icons';
import { useRef } from 'react';
import styled from 'styled-components';

import { Flex } from '@/components/base';
import DefaultItem from '@/components/Common/DefaultItem';
import Tooltip from '@/components/Tooltip/TooltipV2';
import Typo from '@/components/Typography/Typo';
import { CUSTOMER_ANONYMOUS } from '@/configs/reserved-word';
import useColor from '@/hooks/domain/useColor';
import useUserRole from '@/hooks/domain/useUserRole';
import useCustomerStore from '@/stores/CustomerStore';
import useDropdownStore from '@/stores/pure/DropdownStore';
import useToastStore from '@/stores/pure/ToastStore';
import { clipboardCopy } from '@/utils/util';

import CustomerSelectModal from '../CustomerSelectModal';

export type Props = {
  name: string;
  feedbackAlias: string;
  customer_info: CustomerInfoWithAlias | null;
  company_info: CompanyInfoWithAlias | null;
  changeCustomer: (
    feedbackAlias: string,
    customer: CustomerT | null,
    prevState?: {
      customer?: CustomerInfoWithAlias | null;
      company?: CompanyInfoWithAlias | null;
    },
  ) => void;
  view: 'list' | 'detail';
};

const CustomerDropdown = (props: Props) => {
  const { isEditor } = useUserRole();
  const color = useColor();
  const addDefaultToast = useToastStore((state) => state.addDefaultToast);
  const { setCurrDropdown } = useDropdownStore();
  const { addCustomer } = useCustomerStore((state) => ({
    addCustomer: state.addNewCustomer,
  }));
  const dropdownParentRef = useRef<HTMLDivElement>(null);

  const createNewCustomer = async (customer_name: string) => {
    const req: PostCustomerParams = {
      feedbacks: [props.feedbackAlias],
      name: customer_name,
      company: props.company_info?.alias,
    };
    const key = await addCustomer(req);
    return key;
  };

  return (
    <Style ref={dropdownParentRef}>
      {props.view === 'list' && (
        <DefaultItem>
          <Typo
            type="label2"
            weight="r"
            color={props.customer_info?.name ? 'inherit' : color.gray500}
          >
            {props.customer_info?.name || CUSTOMER_ANONYMOUS}
          </Typo>
        </DefaultItem>
      )}

      {props.view === 'detail' && (
        <DetailItemWrapper
          readOnly={!isEditor}
          onClick={() => isEditor && setCurrDropdown(props.name)}
        >
          <Typo
            type="body3"
            color={props.customer_info?.name ? 'inherit' : color.gray500}
          >
            {props.customer_info?.name || CUSTOMER_ANONYMOUS}
          </Typo>
          {props.customer_info?.email && (
            <>
              <Typo type="body3" color={color.gray600}>
                {props.customer_info.email}
              </Typo>
              <Tooltip tooltipNode="Copy email">
                <CopyIconWrapper
                  onClick={(e) => {
                    e.stopPropagation();
                    clipboardCopy(props.customer_info?.email || '');
                    addDefaultToast('Email address copied', 'success');
                  }}
                >
                  <CopyOutlined style={{ color: color.gray600 }} />
                </CopyIconWrapper>
              </Tooltip>
            </>
          )}
        </DetailItemWrapper>
      )}
      <CustomerSelectModal
        name={props.name}
        triggerEl={dropdownParentRef.current}
        createNewCustomer={createNewCustomer}
        toggleItem={(customer) => {
          props.changeCustomer(props.feedbackAlias, customer, {
            customer: props.customer_info,
            company: props.company_info,
          });
          setCurrDropdown(null);
        }}
        company={props.company_info?.alias}
        company_info={props.company_info}
        clearable
      />
    </Style>
  );
};

const Style = styled.div`
  position: relative;
`;

const DetailItemWrapper = styled(Flex).attrs({
  g: '8px',
  a: 'center',
})<{ readOnly: boolean }>`
  padding: 2px 12px;
  border-radius: 34px;
  cursor: ${({ readOnly }) => (readOnly ? 'default' : 'pointer')};
  transition: all 0.2s;
  &:hover {
    background: ${(props) => props.theme.colors.ND04};
  }
`;

const CopyIconWrapper = styled(Flex).attrs({
  a: 'center',
})``;

export default CustomerDropdown;
